import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface VoiceRecognitionAlternative {
  transcript: string;
}

interface VoiceRecognitionResult {
  isFinal: boolean;
  [index: number]: VoiceRecognitionAlternative;
}

interface VoiceRecognitionResultList {
  length: number;
  [index: number]: VoiceRecognitionResult;
}

interface VoiceRecognitionEvent {
  resultIndex: number;
  results: VoiceRecognitionResultList;
}

interface VoiceRecognitionErrorEvent {
  error: string;
}

interface VoiceRecognition {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;
  onresult: ((event: VoiceRecognitionEvent) => void) | null;
  onerror: ((event: VoiceRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

type VoiceRecognitionConstructor = new () => VoiceRecognition;

type SpeechCapableWindow = Window & {
  SpeechRecognition?: VoiceRecognitionConstructor;
  webkitSpeechRecognition?: VoiceRecognitionConstructor;
};

const IGNORED_ERRORS = ["no-speech", "aborted"];

export interface UseSpeechRecognitionReturn {
  isListening: boolean;
  interimTranscript: string;
  error: string | null;
  isSupported: boolean;
  startListening: () => void;
  stopListening: () => Promise<string>;
}

export function useSpeechRecognition(
  lang = "es-MX",
  onAutoEnd?: (transcript: string) => void,
): UseSpeechRecognitionReturn {
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<VoiceRecognition | null>(null);
  const finalTranscriptRef = useRef("");
  const interimRef = useRef("");
  const resolveRef = useRef<((value: string) => void) | null>(null);
  const autoEndRef = useRef(onAutoEnd);

  useEffect(() => {
    autoEndRef.current = onAutoEnd;
  }, [onAutoEnd]);

  const speechWindow = window as SpeechCapableWindow;
  const RecognitionApi = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
  const isSupported = Boolean(RecognitionApi);

  const startListening = useCallback(() => {
    if (!RecognitionApi) {
      setError("unsupported");
      return;
    }

    if (!window.isSecureContext) {
      setError("insecure-context");
      return;
    }

    setError(null);
    finalTranscriptRef.current = "";
    interimRef.current = "";
    setInterimTranscript("");

    const recognition = new RecognitionApi();
    recognition.lang = lang;
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const chunks = Array.from(
        { length: event.results.length - event.resultIndex },
        (_, offset) => event.results[event.resultIndex + offset],
      );

      const finalText = chunks
        .filter((result) => result.isFinal)
        .map((result) => result[0].transcript)
        .join("");
      const interimText = chunks
        .filter((result) => !result.isFinal)
        .map((result) => result[0].transcript)
        .join("");

      finalText && (finalTranscriptRef.current += `${finalText} `);
      interimRef.current = interimText;
      setInterimTranscript(interimText);
    };

    recognition.onerror = (event) => {
      if (IGNORED_ERRORS.includes(event.error)) return;
      setError(event.error);
    };

    recognition.onend = () => {
      const fullTranscript = `${finalTranscriptRef.current}${interimRef.current}`.trim();
      setIsListening(false);
      setInterimTranscript("");
      interimRef.current = "";
      recognitionRef.current = null;

      const resolve = resolveRef.current;
      resolveRef.current = null;
      resolve ? resolve(fullTranscript) : autoEndRef.current?.(fullTranscript);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [RecognitionApi, lang]);

  const stopListening = useCallback(
    () =>
      new Promise<string>((resolve) => {
        const recognition = recognitionRef.current;
        if (!recognition) {
          const fullTranscript = `${finalTranscriptRef.current}${interimRef.current}`.trim();
          setIsListening(false);
          setInterimTranscript("");
          interimRef.current = "";
          resolve(fullTranscript);
          return;
        }

        resolveRef.current = resolve;
        recognition.stop();
      }),
    [],
  );

  return useMemo(
    () => ({
      isListening,
      interimTranscript,
      error,
      isSupported,
      startListening,
      stopListening,
    }),
    [isListening, interimTranscript, error, isSupported, startListening, stopListening],
  );
}

export default useSpeechRecognition;
